'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getAdventureById } from '@/lib/data'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import styles from '@/styles/adventure-page.module.css'
import { BreadcrumbSchema, AdventureSchema, HowToSchema, HOWTO_BOOK_HELISKI } from '@/lib/schema'
import GoogleMapEmbed, { GOOGLE_MAPS_EMBEDS } from '@/components/GoogleMapEmbed'
import BookNowCTA from '@/components/BookNowCTA'


export default function HeliSkiingPage() {
  const thisAdventure = getAdventureById('heli-skiing')
  const t = useTranslations('guidedAdventures')
  const tAdv = useTranslations('adventures')
  const tCommon = useTranslations('common')
  const tLodges = useTranslations('lodges')
  const params = useParams()
  const locale = params.locale as string

  if (!thisAdventure) {
    return <div>{t('common.adventureNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/guided-adventures/` },
        { name: tAdv('heli-skiing.title'), href: `/${locale}/guided-adventures/heli-skiing/` },
      ]} />
      <AdventureSchema
        name={t('heliSkiing.schema.name')}
        description={t('heliSkiing.schema.description')}
        image={thisAdventure.heroImage}
        url={`/${locale}/guided-adventures/heli-skiing/`}
        activityType={t('heliSkiing.schema.activityType')}
        location={t('heliSkiing.schema.location')}
        geo={{ latitude: -33.35, longitude: -70.25 }}
      />
      <HowToSchema {...HOWTO_BOOK_HELISKI} />
      <Header />

      {/* Hero 100vh usando HeroImage - SINCRONIZADO CON HOMEPAGE */}
      <HeroParallax
        backgroundImage={thisAdventure.heroImage}
        title={tAdv('heli-skiing.title')}
        subtitle={tAdv('heli-skiing.subtitle')}
        location={tAdv('heli-skiing.location')}
      />

      <main className={styles.pageWrapper}>
        {/* Adventure Details Section - 3 Column Layout */}
        <section className={styles.adventureDetails}>
          <div className="container">
            <div className={styles.detailsGrid}>
              {/* Left Column */}
              <div className={styles.itineraryColumn}>
                <h3>{t('heliSkiing.sectionTitle')}</h3>
                <p>{t('heliSkiing.intro')}</p>
                <p>{tAdv('heli-skiing.intro')}</p>
              </div>

              {/* Center Column: Map */}
              <div className={styles.mapColumn}>
                <div className={styles.mapWrapper}>
                  <GoogleMapEmbed
                    {...GOOGLE_MAPS_EMBEDS['tres-valles']}
                  />
                </div>
              </div>

              {/* Right Column: What You Can Book */}
              <div className={styles.includedColumn}>
                <h3>{t('heliSkiing.whatYouCanBook')}</h3>
                <ul>
                  <li>{t('heliSkiing.included.item1')}</li>
                  <li>{t('heliSkiing.included.item2')}</li>
                  <li>{t('heliSkiing.included.item3')}</li>
                  <li>{t('heliSkiing.included.item4')}</li>
                  <li>{t('heliSkiing.included.item5')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="inline"
          productId="heli-skiing"
          productType="adventure"
          productName={tAdv('heli-skiing.title')}
          bookingMode={thisAdventure?.bookingMode || 'whatsapp'}
          bookingUrl="/booking/activities/heli-skiing"
        />

        {/* Adventures Section - Carousel */}
        <section className={styles.adventures}>
          <div className="container">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t('common.personalizedAdventures')}</h2>
              <p className={styles.sectionDescription}>
                {t('common.personalizedDescription')}
              </p>
            </div>

            <div className={styles.adventuresCarousel}>
              <Swiper
                modules={[Navigation, Pagination]}
                slidesPerView={1}
                spaceBetween={24}
                loop={true}
                grabCursor={true}
                navigation={{
                  nextEl: `.${styles.swiperButtonNext}`,
                  prevEl: `.${styles.swiperButtonPrev}`,
                }}
                pagination={{
                  el: `.${styles.swiperPagination}`,
                  clickable: true,
                  dynamicBullets: true,
                }}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                    spaceBetween: 24,
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 32,
                  },
                }}
                className={styles.adventuresSwiper}
              >
                {[
                  { id: 0, src: '/images/adventures/heli-skiing/1.webp', alt: t('heliSkiing.images.img1') },
                  { id: 1, src: '/images/adventures/heli-skiing/2.webp', alt: t('heliSkiing.images.img2') },
                  { id: 2, src: '/images/adventures/heli-skiing/3.webp', alt: t('heliSkiing.images.img3') },
                  { id: 3, src: '/images/adventures/heli-skiing/4.webp', alt: t('heliSkiing.images.img4') },
                  { id: 4, src: '/images/adventures/heli-skiing/5.webp', alt: t('heliSkiing.images.img5') },
                  { id: 5, src: '/images/adventures/heli-skiing/6.webp', alt: t('heliSkiing.images.img6') },
                  { id: 6, src: '/images/adventures/heli-skiing/7.webp', alt: t('heliSkiing.images.img7') },
                  { id: 7, src: '/images/adventures/heli-skiing/8.webp', alt: t('heliSkiing.images.img8') },
                ].map((image) => (
                  <SwiperSlide key={`heli-skiing-${image.id}`}>
                    <article className={styles.adventureCard}>
                      <div className={styles.adventureImage}>
                        <img
                          src={image.src}
                          alt={image.alt}
                          loading="lazy"
                        />
                      </div>
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Navigation arrows */}
              <div className={`${styles.swiperButtonPrev} swiper-button-prev`}></div>
              <div className={`${styles.swiperButtonNext} swiper-button-next`}></div>
              <div className={styles.swiperPagination}></div>
            </div>

            <div className={styles.sectionCta}>
              <a href={`/${locale}/guided-adventures/`} className="btn-primary">
                {t('common.exploreAdventures')} &rarr;
              </a>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="section"
          productId="heli-skiing"
          productType="adventure"
          productName={tAdv('heli-skiing.title')}
          bookingMode={thisAdventure?.bookingMode || 'whatsapp'}
          bookingUrl="/booking/activities/heli-skiing"
        />

        {/* Cream Section (Eleven Experience Style) - Lodge Destinations */}
        <section className={styles.creamSection}>
          <div className="container">
            <h2>{t('common.whereToStay')}</h2>
            <div className={styles.lodgesGrid}>
              {/* Apex Mountain Penthouse */}
              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="/images/lodges/apex-mountain-penthouse/IMG_2891.webp"
                    alt={`${tLodges('apex-mountain-penthouse.name')} - ${tLodges('apex-mountain-penthouse.subtitle')}`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-mountain-penthouse.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-mountain-penthouse.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-mountain-penthouse/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>

              {/* Apex Parva Cave */}
              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=1920&q=80"
                    alt={`${tLodges('apex-parva-cave.name')} - ${tLodges('apex-parva-cave.subtitle')}`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-parva-cave.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-parva-cave.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-parva-cave/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="closing"
          productId="heli-skiing"
          productType="adventure"
          productName={tAdv('heli-skiing.title')}
          bookingMode={thisAdventure?.bookingMode || 'whatsapp'}
          bookingUrl="/booking/activities/heli-skiing"
        />

      </main>

      <Footer />
    </>
  )
}
